import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { ROUTE_META } from '@/app/route-meta';
import { setStoredToken, setStoredOrgSlug, clearStoredToken } from '@/app/auth';
import { useApiConfig } from '@/app/ApiProvider';
import { apiFetch } from '@/app/api-fetch';

/**
 * S12 Sign-in. Real password form: POSTs to /v1/auth/login, stores
 * the returned JWT under localStorage('pdm.token'), then navigates to
 * the previous target (?redirect=…) or "/" by default.
 *
 * The seeded dev accounts (admin@example.com / admin, etc) ship with
 * the api-gateway store and the MSW handler — the same credentials
 * work against MSW dev mode and the real backend.
 *
 * ADR 0017 OIDC/WorkOS swap-in lands later: the form's onSubmit body
 * is the only thing that has to change.
 */

type LoginResponse = {
  token: string;
  expires_at?: string;
  user_id?: string;
  email?: string;
  display_name?: string;
  role?: string;
  normalized_role?: string;
  tenant_id?: string;
  tenant_slug?: string;
  capabilities?: string[];
};

type LoginSearch = { redirect?: string };

export const Route = createFileRoute('/auth')({
  staticData: ROUTE_META.fullscreen,
  validateSearch: (raw): LoginSearch => {
    const redirect = typeof raw?.redirect === 'string' ? raw.redirect : undefined;
    return redirect ? { redirect } : {};
  },
  component: AuthScreen,
});

function AuthScreen() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/auth' });
  const queryClient = useQueryClient();
  const apiConfig = useApiConfig();
  const [email, setEmail] = useState('admin@example.com');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: async (creds: { email: string; password: string }) => {
      const res = await apiFetch(apiConfig, '/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(creds),
      });
      if (!res.ok) {
        let msg = 'Sign-in failed';
        try {
          const body = (await res.json()) as { error?: string };
          if (body?.error) msg = body.error;
        } catch {
          /* non-JSON body — keep generic message */
        }
        throw new Error(msg);
      }
      return (await res.json()) as LoginResponse;
    },
    onSuccess: (data) => {
      setStoredToken(data.token);
      // Tenant slug is the URL prefix for everything tenant-scoped.
      // Default to tenant_id when the backend hasn't started minting
      // tenant_slug yet — pre-rollout safety.
      const slug = data.tenant_slug ?? data.tenant_id ?? '';
      if (slug) setStoredOrgSlug(slug);
      // Drop any stale caches from a previous session (capabilities,
      // /me, etc.) so the next render fetches as the new user.
      queryClient.clear();
      // Send the user where they were headed, or to their org home.
      const target = search.redirect && search.redirect.startsWith('/') ? search.redirect : slug ? `/${slug}/` : '/';
      navigate({ to: target });
    },
    onError: (err) => {
      setErrorMsg(err instanceof Error ? err.message : 'Sign-in failed');
    },
  });

  // Landing on /auth means there's no live session. Clearing any
  // residual token defends against the edge case where the JWT exists
  // but is expired — otherwise the next /me roundtrip would 401-loop
  // through the guard. Runs once on mount; the user is about to type
  // fresh credentials anyway.
  useEffect(() => {
    clearStoredToken();
  }, []);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg(null);
    login.mutate({ email: email.trim(), password });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-sp-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-r-card border border-gray-300 bg-gray-50 p-sp-9"
      >
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          S12
        </p>
        <h1 className="mt-sp-2 text-fs-h1 font-medium text-gray-900">Sign in</h1>
        <p className="mt-sp-3 text-fs-body text-gray-700">
          Use your PDM account.
        </p>

        <label className="mt-sp-6 block text-fs-meta font-medium text-gray-900">
          Email
          <input
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-sp-1 block w-full rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2 text-fs-body text-gray-900 focus:border-info focus:outline-none"
          />
        </label>

        <label className="mt-sp-4 block text-fs-meta font-medium text-gray-900">
          Password
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-sp-1 block w-full rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2 text-fs-body text-gray-900 focus:border-info focus:outline-none"
          />
        </label>

        {errorMsg ? (
          <p
            role="alert"
            className="mt-sp-4 rounded-r-default border border-danger bg-danger-bg px-sp-3 py-sp-2 text-fs-meta text-danger"
          >
            {errorMsg}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={login.isPending}
          className="mt-sp-6 w-full rounded-r-default bg-info px-sp-4 py-sp-2 text-fs-body font-medium text-gray-50 disabled:opacity-60"
        >
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </button>

        {import.meta.env.DEV ? (
          <details className="mt-sp-6 text-fs-meta text-gray-700">
            <summary className="cursor-pointer">Dev accounts</summary>
            <ul className="mt-sp-2 list-disc pl-sp-4">
              <li>
                <code>admin@example.com</code> / <code>admin</code>
              </li>
              <li>
                <code>manager@example.com</code> / <code>manager</code>
              </li>
              <li>
                <code>maintainer@example.com</code> / <code>maintainer</code>
              </li>
              <li>
                <code>viewer@example.com</code> / <code>viewer</code>
              </li>
              <li>
                <code>radiant@example.com</code> / <code>radiant</code>
              </li>
            </ul>
          </details>
        ) : null}
      </form>
    </div>
  );
}
